/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Formik, FormikHelpers } from 'formik'
import { Button, FormGroup, FormLabel } from 'react-bootstrap'
import { isEqual, difference } from 'lodash'
import { InfiniteScrollSelect } from '../InfiniteScrollSelect/InfiniteScrollSelect'
import { transformToSelectOptions } from '../../utils/selectOptions'
import { useServiceContext } from '../../root'

interface FsiTagsProps {
  fsi: any
  isModal?: boolean
  doClose?: () => void
  params?: any
}

interface TagFormValues {
  fsiTags: any[]
}

interface Tag {
  id: number
  name: string
  type?: string
  propertyType?: string
  color?: string
  description?: string
}

/**
 * Tag management component for files
 * Allows assigning/removing tags with infinite scroll picker
 */
export function FsiTags(props: FsiTagsProps) {
  const { fsi, isModal, doClose, params } = props
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tagsApi = context.api.tags // Access tags API from context
  const toast = context.service.toast

  const [fsiTags, setFsiTags] = useState<any[]>([])
  const [initialTags, setInitialTags] = useState<any[]>([])
  const [canManageTags, setCanManageTags] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [isBulkOperation, setIsBulkOperation] = useState<boolean>(false)
  const [isSupersededTagPresent, setIsSupersededTagPresent] = useState<boolean>(false)

  // Helper to check if a tag is SYSTEM type
  const checkIfTagIsOfTypeSystem = (item: any) => {
    return item?.type ? item.type === 'SYSTEM' : item?.propertyType === 'SYSTEM'
  }

  // Check if this is a bulk operation (multiple files selected)
  useEffect(() => {
    const hasBulkParams = params?.selectedIds && params.selectedIds.length > 1
    setIsBulkOperation(hasBulkParams)

    // For bulk operations, default to allowing tag management
    // (the menu action already checked permissions)
    if (hasBulkParams) {
      setCanManageTags(true)
    }
  }, [params])

  // Load tags on mount
  useEffect(() => {
    if (!fsi?.id) {
      // For bulk operations, we don't load existing tags (they may differ across files)
      if (isBulkOperation) {
        setIsLoading(false)
        return
      }

      setError('No file ID provided')
      setIsLoading(false)
      return
    }

    const loadTags = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const tags = await tagsApi.getTagsByFsi(fsi.id)
        const formattedTags = transformToSelectOptions(tags, 'id', 'name', null, true)
        setFsiTags(formattedTags)
        setInitialTags(formattedTags)
        setIsSupersededTagPresent(!!tags?.some((item: any) => checkIfTagIsOfTypeSystem(item)))
      } catch (error) {
        const errorMsg = translate('FAILED_TO_LOAD_TAGS') || 'Failed to load tags'
        setError(errorMsg)
        toast.error(errorMsg)
      } finally {
        setIsLoading(false)
      }
    }

    // Only load tags for single file operations
    if (!isBulkOperation) {
      loadTags()
    }
  }, [fsi?.id, isBulkOperation])

  // Check permissions
  useEffect(() => {
    // Skip permission check if already set by bulk operation logic
    if (isBulkOperation) {
      return
    }

    if (fsi?.actionFlags) {
      setCanManageTags(fsi.actionFlags.canManageTags || false)
    }
  }, [fsi?.actionFlags, isBulkOperation])

  // Subscribe to tag updates
  useEffect(() => {
    const handleTagUpdate = (event: CustomEvent) => {
      if (event.detail?.fsiId === fsi?.id) {
        // Reload tags
        tagsApi.getTagsByFsi(fsi.id).then((tags) => {
          const formattedTags = transformToSelectOptions(tags, 'id', 'name', null, true)
          setFsiTags(formattedTags)
          setInitialTags(formattedTags)
          setIsSupersededTagPresent(!!tags?.some((item: any) => checkIfTagIsOfTypeSystem(item)))
        })
      }
    }

    window.addEventListener('fileupdate:tags' as any, handleTagUpdate)
    return () => {
      window.removeEventListener('fileupdate:tags' as any, handleTagUpdate)
    }
  }, [fsi?.id])

  // Async load function for InfiniteScrollSelect
  const asyncLoadFn = useCallback(
    async (search: string, pageStart: number) => {
      try {
        const response = await tagsApi.getOptionsForAsyncPicker(search, pageStart)
        // Filter out SYSTEM tags from picker options
        const filteredOptions = response.options?.filter((item: any) => !checkIfTagIsOfTypeSystem(item)) || []
        const formattedOptions = transformToSelectOptions(filteredOptions, 'id', 'name', null, true)
        // If no options, set hasMore to false to prevent infinite loading
        const hasMore = formattedOptions.length > 0 && response.hasMore
        return {
          options: formattedOptions,
          hasMore,
          additional: {
            page: pageStart + 25
          }
        }
      } catch (error) {
        console.error('Error loading tag options:', error)
        return {
          options: [],
          hasMore: false
        }
      }
    },
    [checkIfTagIsOfTypeSystem]
  )

  const handleSubmit = async (values: TagFormValues, formikHelpers: FormikHelpers<TagFormValues>) => {
    const { setSubmitting } = formikHelpers

    try {
      // Detect added and removed tags
      const initialIds = initialTags.map((t) => t.value)
      const currentIds = values.fsiTags.map((t) => t.value)
      const addedIds = difference(currentIds, initialIds)
      const removedIds = difference(initialIds, currentIds)

      if (addedIds.length === 0 && removedIds.length === 0) {
        toast.info('No changes to save')
        setSubmitting(false)
        return
      }

      // Determine file IDs - use selectedIds for bulk operations, otherwise single file
      const fileIds = isBulkOperation && params?.selectedIds ? params.selectedIds : [fsi.id]

      // Call API to assign tags
      await tagsApi.assignTagsToFsis(fileIds, addedIds, removedIds)

      // Update initial state
      setInitialTags(values.fsiTags)

      // Emit event for each file
      fileIds.forEach((fileId: number) => {
        window.dispatchEvent(
          new CustomEvent('fileupdate:tags', {
            detail: { fsiId: fileId }
          })
        )
      })

      toast.success(isBulkOperation ? 'Tags updated successfully for selected files' : 'Tags updated successfully')

      // Close modal if in modal mode
      if (isModal && doClose) {
        doClose()
      }
    } catch (error: any) {
      console.error('Error saving tags:', error)

      // Check for superseded error
      if (error?.code === 409) {
        toast.error('This file has been modified. Please refresh and try again.')
      } else {
        toast.error('Failed to update tags')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (isModal && doClose) {
      doClose()
    }
  }

  return (
    <>
      {isLoading && (
        <div className={isModal ? 'modal-body' : 'panel-body'}>
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading tags...</span>
            </div>
            <p>Loading tags...</p>
          </div>
        </div>
      )}

      {error && (
        <div className={isModal ? 'modal-body' : 'panel-body'}>
          <div className="alert alert-danger">{error}</div>
        </div>
      )}

      {!isLoading && !error && (
        <Formik<TagFormValues> initialValues={{ fsiTags }} onSubmit={handleSubmit} enableReinitialize>
          {({ values, setFieldValue, isSubmitting, submitForm, resetForm }) => {
            const hasChanges = !isEqual(
              values.fsiTags.map((t) => t.value).sort(),
              initialTags.map((t) => t.value).sort()
            )

            // Prevent SYSTEM tag removal: if SYSTEM tag present, block its removal
            const handleTagChange = (newValue: any) => {
              let updated = newValue || []
              if (isSupersededTagPresent && values.fsiTags?.some((item: any) => checkIfTagIsOfTypeSystem(item))) {
                // Find removed tag
                const removedTagId = difference(
                  values.fsiTags.map((t: any) => t.value),
                  updated.map((t: any) => t.value)
                )
                const removedTagInfo = values.fsiTags.find(
                  (item: any) => item?.id === removedTagId[0] || item?.value === removedTagId[0]
                )
                if (removedTagInfo && checkIfTagIsOfTypeSystem(removedTagInfo)) {
                  // Re-add SYSTEM tag
                  updated = [...updated, removedTagInfo]
                }
              }
              setFieldValue('fsiTags', updated)
            }

            return (
              <>
                <div className={isModal ? 'modal-body' : 'panel-body'}>
                  {isModal && (
                    <FormGroup className="wparag">
                      <FormLabel>{translate('DOCUMENT')}</FormLabel>
                      <p className="whitespaceprewrap fs12">
                        {isBulkOperation ? translate('MULTIPLE_DOCS') : fsi?.name}
                      </p>
                    </FormGroup>
                  )}
                  <FormGroup controlId="a030c4ad-b4ae-4e4c-b86c-110a237cb24a" className="w50p">
                    <FormLabel>{translate('DOC_TAGS_LABEL')}</FormLabel>
                    <InfiniteScrollSelect
                      placeholder={translate('SELECT_TAGS_PLACEHOLDER')}
                      name="fsiTags"
                      value={values.fsiTags}
                      asyncLoadFn={asyncLoadFn}
                      onChange={handleTagChange}
                      isMulti
                      searchable
                      isDisabled={!canManageTags}
                      id="a030c4ad-b4ae-4e4c-b86c-110a237cb24a"
                      isClearable={!isSupersededTagPresent}
                      isFromTags
                    />
                  </FormGroup>
                </div>

                {!isModal && (
                  <div className="panel-footer text-right">
                    {canManageTags ? (
                      <>
                        <Button
                          variant="link"
                          onClick={() => resetForm()}
                          disabled={isSubmitting || !hasChanges}
                          data-action="cancel"
                          data-test-id="cancel"
                        >
                          {translate('CANCEL')}
                        </Button>
                        <Button
                          className="btn-chita"
                          variant="primary"
                          onClick={submitForm}
                          disabled={isSubmitting || !hasChanges}
                          data-action="save"
                          data-test-id="save"
                        >
                          {isSubmitting ? translate('SAVING') : translate('SAVE_BTN')}
                        </Button>
                      </>
                    ) : null}
                  </div>
                )}

                {isModal && canManageTags && (
                  <div className="modal-footer">
                    <Button
                      data-action="cancel"
                      variant="link"
                      onClick={handleCancel}
                      disabled={isSubmitting || !hasChanges}
                    >
                      {translate('CANCEL')}
                    </Button>
                    <Button
                      data-action="save"
                      className="btn btn-primary btn-chita"
                      onClick={submitForm}
                      disabled={isSubmitting || !hasChanges}
                    >
                      {isSubmitting ? translate('SAVING') : translate('SAVE_BTN')}
                    </Button>
                  </div>
                )}
              </>
            )
          }}
        </Formik>
      )}
    </>
  )
}

export default FsiTags
